import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './IntroVideo.css';
import { useCharacter } from '../context/CharacterContext';
import { hasSeenIntroVideo, markIntroVideoSeen } from '../utils/introVideo';
import { pauseAppBackgroundMusic } from '../utils/appMusic';

const SKIP_DELAY_MS = 5000;
const PLAY_FALLBACK_MS = 3000;
const INTRO_VIDEO_SRC = `${process.env.PUBLIC_URL || ''}/video/giris.mp4`;

const IntroVideo = () => {
  const { t } = useTranslation('selection');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isReplay = searchParams.get('replay') === '1';
  const videoRef = useRef(null);
  const playAttemptedRef = useRef(false);
  const skipTimerRef = useRef(null);
  const { loading: characterLoading, hasCharacter } = useCharacter();
  const username = localStorage.getItem('username');
  const [isLoading, setIsLoading] = useState(true);
  const [skipVisible, setSkipVisible] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const shouldShow = Boolean(username) && (!hasSeenIntroVideo(username) || isReplay);

  const finishIntro = useCallback(() => {
    pauseAppBackgroundMusic();
    videoRef.current?.pause();
    if (username) {
      markIntroVideoSeen(username);
    }
    navigate('/character-selection', { replace: true });
  }, [navigate, username]);

  useEffect(() => {
    if (!username) {
      navigate('/login', { replace: true });
      return;
    }
    if (!characterLoading && hasCharacter) {
      navigate('/game', { replace: true });
      return;
    }
    if (hasSeenIntroVideo(username) && !isReplay) {
      navigate('/character-selection', { replace: true });
    }
  }, [username, navigate, characterLoading, hasCharacter, isReplay]);

  const hideLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  const showSkipSoon = useCallback(() => {
    if (skipTimerRef.current) return;
    skipTimerRef.current = window.setTimeout(() => {
      setSkipVisible(true);
    }, SKIP_DELAY_MS);
  }, []);

  useEffect(() => {
    if (!shouldShow) return undefined;

    playAttemptedRef.current = false;
    setIsLoading(true);
    setSkipVisible(false);
    setLoadError(false);
    pauseAppBackgroundMusic();
    showSkipSoon();

    let disposed = false;
    let fallbackTimer = null;
    let video = null;

    const attemptPlay = () => {
      if (disposed || !video || playAttemptedRef.current) return;
      playAttemptedRef.current = true;
      pauseAppBackgroundMusic();
      video.muted = false;
      video.volume = 1;
      video.play()
        .then(() => hideLoading())
        .catch(() => {
          playAttemptedRef.current = false;
          video.muted = true;
          video.play()
            .then(hideLoading)
            .catch(() => {
              hideLoading();
              setLoadError(true);
              setSkipVisible(true);
            });
        });
    };

    const onReady = () => {
      if (disposed) return;
      attemptPlay();
    };

    const onPlaying = () => {
      if (disposed) return;
      hideLoading();
    };

    const onError = () => {
      if (disposed) return;
      hideLoading();
      setLoadError(true);
      setSkipVisible(true);
    };

    const bindVideo = () => {
      video = videoRef.current;
      if (!video) return false;

      video.addEventListener('loadeddata', onReady);
      video.addEventListener('canplay', onReady);
      video.addEventListener('playing', onPlaying);
      video.addEventListener('error', onError);

      fallbackTimer = window.setTimeout(() => {
        if (disposed) return;
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          attemptPlay();
          hideLoading();
        }
      }, PLAY_FALLBACK_MS);

      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        onReady();
      }
      return true;
    };

    const frameId = requestAnimationFrame(() => {
      if (!bindVideo()) {
        window.setTimeout(() => {
          if (!disposed) bindVideo();
        }, 50);
      }
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (video) {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('canplay', onReady);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('error', onError);
      }
      if (skipTimerRef.current) {
        clearTimeout(skipTimerRef.current);
        skipTimerRef.current = null;
      }
    };
  }, [shouldShow, isReplay, hideLoading, showSkipSoon]);

  if (!shouldShow) {
    return null;
  }

  const content = (
    <div className="intro-video">
      <video
        ref={videoRef}
        className="intro-video__player"
        src={INTRO_VIDEO_SRC}
        data-intro-video="true"
        playsInline
        autoPlay
        preload="auto"
        onEnded={finishIntro}
        onPlaying={hideLoading}
      />
      {isLoading && (
        <div className="intro-video__loading" role="status" aria-live="polite">
          <div className="intro-video__loading-text">{tc('loading')}</div>
        </div>
      )}
      {loadError && (
        <div className="intro-video__error">{t('introLoadFailed')}</div>
      )}
      {skipVisible && (
        <button
          type="button"
          className="intro-video__skip intro-video__skip--visible"
          onClick={finishIntro}
        >
          {t('introSkip')}
        </button>
      )}
    </div>
  );

  return createPortal(content, document.body);
};

export default IntroVideo;
